import { Badge } from "@/components/ui/badge.jsx";
import { getStatusBadgeClass } from "@/lib/status-utils.js";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion.jsx";
import { AlertTriangle } from "lucide-react";
import { parseCylinderFlag } from "./optimization-flag-utils.js";

/** Icon well tints mirror the status pill palette (severe/warning/ok). */
const ICON_WELL_CLASS = {
  severe: "bg-destructive/10 text-destructive dark:bg-destructive/15 dark:text-red-300",
  warning:
    "bg-asset-warning/10 text-amber-800 dark:bg-asset-warning/15 dark:text-amber-300",
  ok: "bg-asset-running/10 text-green-700 dark:bg-asset-running/15 dark:text-green-400",
};

/** Neutralizes the Accordion's brand-red wash so severity colors stay readable. */
const FLAG_ACCORDION_CONTENT_CLASS = "border-l-0 bg-transparent border-border";

/** Match AccordionTrigger's `[&[data-state=open]]` specificity so open state is cyan, not primary red. */
const FLAG_ACCORDION_TRIGGER_CLASS =
  "flex items-center gap-3 py-3 text-foreground hover:bg-muted/50 hover:text-foreground hover:no-underline [&[data-state=open]]:text-brand-cyan [&>svg:last-child]:text-muted-foreground [&[data-state=open]>svg:last-child]:rotate-180 [&[data-state=open]>svg:last-child]:text-muted-foreground";

/**
 * @param {{ accordionValue: string, label: string, Icon: import("lucide-react").LucideIcon, diagnostics: unknown }} props
 */
export default function FlagCategorySection({
  accordionValue,
  label,
  Icon,
  diagnostics,
}) {
  const hasStructured =
    diagnostics
    && typeof diagnostics === "object"
    && Array.isArray(diagnostics.flags)
    && diagnostics.flags.length > 0;

  if (hasStructured) {
    const d = diagnostics;
    return (
      <AccordionItem value={accordionValue} className='border-border'>
        <AccordionTrigger className={FLAG_ACCORDION_TRIGGER_CLASS}>
          <span
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
              d.severeCount > 0
                ? ICON_WELL_CLASS.severe
                : d.warningCount > 0
                  ? ICON_WELL_CLASS.warning
                  : ICON_WELL_CLASS.ok
            }`}>
            <Icon className='h-4 w-4' />
          </span>
          <span className='flex-1 text-left font-medium'>{label}</span>
          <div className='flex shrink-0 items-center gap-1.5'>
            {d.severeCount > 0 && (
              <Badge
                variant='status'
                className={getStatusBadgeClass("offline", "shrink-0")}>
                {d.severeCount} Severe
              </Badge>
            )}
            {d.warningCount > 0 && (
              <Badge
                variant='status'
                className={getStatusBadgeClass("alerted", "shrink-0")}>
                {d.warningCount} Warning
              </Badge>
            )}
            {d.severeCount === 0 && d.warningCount === 0 && (
              <Badge
                variant='status'
                className={getStatusBadgeClass("normal", "shrink-0")}>
                {d.status}
              </Badge>
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent className={FLAG_ACCORDION_CONTENT_CLASS}>
          <ul className='space-y-2 pt-1'>
            {d.flags.map((flag, i) => {
              const parsed = parseCylinderFlag(flag);
              const triangleClass = parsed.isWarning
                ? "h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400"
                : "h-4 w-4 shrink-0 text-red-600 dark:text-red-400";
              return (
                <li
                  key={i}
                  className='flex items-center gap-2 text-sm text-foreground'>
                  <AlertTriangle className={triangleClass} />
                  <span className='flex-1'>
                    {parsed.identifier ? (
                      <>
                        <span className='border-b border-dotted border-border pb-0.5'>
                          {parsed.identifier}
                        </span>
                        <span> - {parsed.flagName}</span>
                      </>
                    ) : (
                      parsed.text
                    )}
                  </span>
                  {parsed.name && (
                    <span className='shrink-0 rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground'>
                      {parsed.name}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </AccordionContent>
      </AccordionItem>
    );
  }

  const legacyStatus = typeof diagnostics === "string" ? diagnostics : null;
  const hasLegacyIssue = legacyStatus != null && legacyStatus !== "OK";

  if (hasLegacyIssue) {
    const iconSpan = (
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${ICON_WELL_CLASS.severe}`}>
        <Icon className='h-4 w-4' />
      </span>
    );
    return (
      <AccordionItem value={accordionValue} className='border-border'>
        <AccordionTrigger className={FLAG_ACCORDION_TRIGGER_CLASS}>
          {iconSpan}
          <span className='flex-1 text-left font-medium'>{label}</span>
          <Badge
            variant='status'
            className={getStatusBadgeClass("offline", "shrink-0")}>
            {legacyStatus}
          </Badge>
        </AccordionTrigger>
        <AccordionContent className={FLAG_ACCORDION_CONTENT_CLASS}>
          <p className='text-sm text-muted-foreground pt-1'>See status above.</p>
        </AccordionContent>
      </AccordionItem>
    );
  }

  return (
    <div className='flex items-center gap-3 border-b border-border py-3'>
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${ICON_WELL_CLASS.ok}`}>
        <Icon className='h-4 w-4' />
      </span>
      <span className='flex-1 font-medium text-foreground'>{label}</span>
      <Badge
        variant='status'
        className={getStatusBadgeClass("normal", "shrink-0")}>
        OK
      </Badge>
    </div>
  );
}
